import { useEffect, useState } from 'react';

import {
    isPriceSetting,
    priceSettingValue,
    TRIGGER_OFF,
} from '../utils/triggerSetting';

export interface TriggerPriceMode {
    /** true while the control is showing its price field rather than presets */
    priceMode: boolean;
    /** raw text in the field; may be empty or half-typed */
    draft: string;
    /** switch to typing a price. Disarms the trigger until a number parses. */
    enterPriceMode: () => void;
    /** back to the presets, arming the one given */
    choosePreset: (preset: string) => void;
    /** the field changed */
    setDraft: (raw: string) => void;
}

/**
 * The behaviour behind "set this trigger at a price instead of a percentage",
 * shared by the desktop rail and the mobile config panel.
 *
 * Only the behaviour: the two surfaces draw their controls differently (Chips
 * on the rail, a ToggleGroup in the panel), and making them share markup would
 * mean changing how one of them looks. Sharing the state is what stops them
 * disagreeing about what "armed" means, which is exactly how the rail ended up
 * without price entry after the panel got it.
 *
 * Entering price mode disarms the trigger deliberately. Carrying the previous
 * preset silently underneath an empty field would leave a trigger armed at a
 * level the user did not choose and can no longer see.
 */
export function useTriggerPriceMode(
    value: string,
    onChange: (next: string) => void,
): TriggerPriceMode {
    const [priceMode, setPriceMode] = useState(() => isPriceSetting(value));
    const [draft, setDraftState] = useState(() =>
        isPriceSetting(value) ? value : '',
    );

    // a price arriving from elsewhere (a restored config, the other surface)
    // puts this control into price mode showing that price
    useEffect(() => {
        if (isPriceSetting(value) && value !== draft) {
            setPriceMode(true);
            setDraftState(value);
        }
    }, [value, draft]);

    return {
        priceMode,
        draft,
        enterPriceMode: () => {
            setPriceMode(true);
            setDraftState('');
            onChange(TRIGGER_OFF);
        },
        choosePreset: (preset: string) => {
            setPriceMode(false);
            setDraftState('');
            onChange(preset);
        },
        setDraft: (raw: string) => {
            setDraftState(raw);
            const parsed = Number.parseFloat(raw);
            onChange(
                Number.isFinite(parsed) && parsed > 0
                    ? priceSettingValue(parsed)
                    : TRIGGER_OFF,
            );
        },
    };
}
